import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { formatTitle } from '../hooks/useDocumentTitle';

/**
 * 按当前 pathname 集中设置浏览器标签页标题（静态路由）。
 *
 * 数据驱动页给一个通用兜底（如「数据集详情」「搜索」），随后页面内用 useDocumentTitle
 * 覆盖为精确标题；因本组件在 <Routes> 之前渲染，其 effect 先执行、页面 effect 后执行，
 * 故数据就绪时页面标题稳定胜出。未匹配的路径（404）→「页面未找到」。
 *
 * 新增路由时请在此登记标题；否则该页标签页会显示「页面未找到」。
 */

// [路径模板, 标题]；title 为 null 表示首页（用站点名 + 标语）。matchPath 精确匹配，故顺序无关。
const ROUTE_TITLES: ReadonlyArray<readonly [string, string | null]> = [
  ['/', null],
  ['/datasets', '全部数据集'],
  ['/datasets/:code', '数据集详情'],
  ['/search', '搜索'],
  ['/scan-login', '扫码登录'],
  ['/pricing', '会员订阅'],
  ['/redeem', '兑换码'],
  ['/about', '关于我们'],
  ['/data-sources', '数据来源'],
  ['/faq', '常见问题'],
  ['/terms', '服务条款'],
  ['/forgot-password', '找回密码'],
  ['/reset-password', '重置密码'],
  ['/verify-email', '邮箱验证'],
  // /library 系列是历史重定向，给个标题避免跳转瞬间闪「页面未找到」
  ['/library', '全部数据集'],
  ['/library/:code', '全部数据集'],
  ['/profile', '账户设置'],
  ['/profile/datasets', '我的数据集'],
  ['/profile/downloads', '下载记录'],
  ['/admin/login', '管理员登录'],
  ['/admin', '管理后台'],
  ['/admin/categories', '分类管理'],
  ['/admin/datasets', '数据集管理'],
  ['/admin/links', '链接管理'],
  ['/admin/redemption-codes', '兑换码管理'],
  ['/admin/user-groups', '会员套餐'],
  ['/admin/users', '用户管理'],
  ['/admin/audit-logs', '审计日志'],
  ['/admin/jobs', '处理任务'],
];

export function RouteTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const found = ROUTE_TITLES.find(([pattern]) =>
      matchPath({ path: pattern, end: true }, pathname),
    );
    document.title = formatTitle(found ? found[1] : '页面未找到');
  }, [pathname]);
  return null;
}
